import { useState } from "react";
import {
  Box,
  Button,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Stack,
} from "@mui/material";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import { ChannelList } from "stream-chat-react";
import { Resources, useResource } from "Translations/Resources";
import type { Channel, ChannelOptions, ChannelSort } from "stream-chat";
import type { ChatChannelFilters } from "Types/Chat/chatFilters";
import { ChatCategoryFilter } from "Components/Chat/ChatCategoryFilter";
import { ChatSearchField } from "Components/Chat/ChatSearchField";
import { ChatChannelPreview } from "Components/Chat/ChatChannelPreview";
import { ChatRequestPreview } from "Components/Chat/ChatRequestPreview";
import { ChatRejectedPreview } from "Components/Chat/ChatRejectedPreview";
import { ChannelListItemsSkeleton } from "Components/Chat/ChatLoadingSkeleton";
import { ChatChannelListContainer } from "Components/Chat/ChatChannelListContainer";
import { ChatChannelListEmpty } from "Components/Chat/ChatChannelListEmpty";
import { ChatChannelSearchEmpty } from "Components/Chat/ChatChannelSearchEmpty";
import type { ChatChannelCategory } from "Components/Chat/chatChannelCategories";

const SORT: ChannelSort = { last_message_at: -1 };
const CHANNEL_OPTIONS: ChannelOptions = { limit: 20 };
const RESOURCES = Resources.Chat.Requests;

type Props = {
  isMobile: boolean;
  isChannelOpen: boolean;
  selectedCategory: ChatChannelCategory;
  onCategoryChange: (category: ChatChannelCategory) => void;
  filters: ChatChannelFilters;
  channelRenderFilterFn: (channels: Channel[]) => Channel[];
  /** The render filter is still waiting on data of its own — the list is unanswered, not empty. */
  isChannelFilterPending: boolean;
  onNewChatClick: () => void;
  onNewGroupChatClick: () => void;
  searchText: string;
  onSearchChange: (text: string) => void;
  /** A term is typed: the search spans every category, so the tabs step aside until it clears. */
  isSearching: boolean;
  pendingRequestCount: number;
  isShowingRejected: boolean;
  onToggleRejected: (value: boolean) => void;
};

export const ChatChannelListPanel = ({
  isMobile,
  isChannelOpen,
  selectedCategory,
  onCategoryChange,
  filters,
  channelRenderFilterFn,
  isChannelFilterPending,
  onNewChatClick,
  onNewGroupChatClick,
  searchText,
  onSearchChange,
  isSearching,
  pendingRequestCount,
  isShowingRejected,
  onToggleRejected,
}: Props) => {
  const { t } = useResource();
  const isRequests = selectedCategory === "Requests" && !isSearching;
  // Controlled so the dial opens on click only: MUI's default also opens on hover/focus, which
  // on desktop pops the actions the moment the pointer crosses the FAB. Blur and Escape still
  // close it; only the hover-driven open/close reasons are ignored.
  const [isDialOpen, setIsDialOpen] = useState(false);

  const openDialogFromDial = (openDialog: () => void) => () => {
    setIsDialOpen(false);
    openDialog();
  };

  if (isMobile && isChannelOpen) {
    return null;
  }

  const getEmptyStateComponent = () => {
    if (isChannelFilterPending) {
      return ChannelListItemsSkeleton;
    }
    return isSearching ? ChatChannelSearchEmpty : ChatChannelListEmpty;
  };

  const getPreviewComponent = () => {
    if (!isRequests) {
      return ChatChannelPreview;
    }
    return isShowingRejected ? ChatRejectedPreview : ChatRequestPreview;
  };

  return (
    <Stack
      height="100%"
      width={isMobile ? "100%" : 400}
      minWidth={isMobile ? 0 : 400}
      overflow="hidden"
      borderRight={isMobile ? "none" : "1px solid"}
      borderColor="divider"
      position="relative"
      gap={3}
    >
      <Stack px={2} pt={isMobile ? 1 : 3} gap={3}>
        {!isSearching && (
          <ChatCategoryFilter
            value={selectedCategory}
            onChange={onCategoryChange}
            pendingRequestCount={pendingRequestCount}
          />
        )}
        <ChatSearchField
          value={searchText}
          onChange={onSearchChange}
          placeholder={t(Resources.Chat.Layout.SearchPlaceholder)}
        />
        {isRequests && (
          <Button
            size="small"
            variant="text"
            onClick={() => onToggleRejected(!isShowingRejected)}
            sx={{ alignSelf: "flex-end", textTransform: "none" }}
          >
            {t(
              isShowingRejected
                ? RESOURCES.ShowPending
                : RESOURCES.ShowRejected,
            )}
          </Button>
        )}
      </Stack>
      <Box flex={1} minHeight={0}>
        <ChannelList
          filters={filters}
          sort={SORT}
          options={CHANNEL_OPTIONS}
          setActiveChannelOnMount={false}
          channelRenderFilterFn={channelRenderFilterFn}
          // Our List keeps the previous rows visible during a reload instead of the SDK's
          // skeleton swap; it needs the loaded channels to tell rows from an empty state.
          List={ChatChannelListContainer}
          sendChannelsToList
          Preview={getPreviewComponent()}
          LoadingIndicator={ChannelListItemsSkeleton}
          // The SDK is done loading, but our own render filter may not be — keep the skeleton
          // until it can answer, so a pending category never reads as an empty one.
          EmptyStateIndicator={getEmptyStateComponent()}
        />
      </Box>
      {!isRequests && (
        // One FAB, two actions: the dial expands into labelled 1:1 / group choices. Labels stay
        // open because there is no hover to reveal them on touch screens. The static label has
        // no intrinsic width, so without nowrap it breaks on every space and the two labels
        // stack into each other.
        <SpeedDial
          ariaLabel={t(Resources.Chat.NewChat.Title)}
          icon={<SpeedDialIcon />}
          direction="up"
          open={isDialOpen}
          onOpen={(_, reason) => {
            if (reason === "toggle") {
              setIsDialOpen(true);
            }
          }}
          onClose={(_, reason) => {
            if (reason !== "mouseLeave") {
              setIsDialOpen(false);
            }
          }}
          sx={{ position: "absolute", bottom: 24, right: 24 }}
        >
          <SpeedDialAction
            icon={<GroupAddOutlinedIcon />}
            onClick={openDialogFromDial(onNewGroupChatClick)}
            slotProps={{
              tooltip: {
                title: t(Resources.Chat.NewChat.GroupTitle),
                open: true,
              },
              staticTooltipLabel: { sx: { whiteSpace: "nowrap" } },
            }}
          />
          <SpeedDialAction
            icon={<EditNoteOutlinedIcon />}
            onClick={openDialogFromDial(onNewChatClick)}
            slotProps={{
              tooltip: {
                title: t(Resources.Chat.NewChat.DirectTitle),
                open: true,
              },
              staticTooltipLabel: { sx: { whiteSpace: "nowrap" } },
            }}
          />
        </SpeedDial>
      )}
    </Stack>
  );
};
